import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Modal } from '@heroui/react';
import { DatePickerField } from '@/components/datePickerField';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type EditorToolbarProps = {
    /** The version number a publish would create — `Publish v{n}`. */
    nextVersion: number;
    effectiveFrom: string;
    onEffectiveFromChange: (value: string) => void;
    /** Server refusal anchored to the date field. */
    effectiveFromError: string | null;
    /** A BranchAdmin gets History only — there is nothing here for them to save. */
    canWrite: boolean;
    /** Save draft is live only while there is an edit to save. */
    isDirty: boolean;
    isSaving: boolean;
    isPublishing: boolean;
    onOpenHistory: () => void;
    onSaveDraft: () => void;
    onPublish: () => void;
};

/**
 * Everything you can do to this template, in one strip beside its name.
 *
 * The date and the two ways out sit together because they are one decision:
 * `Publish v4` and `in force from 01/09/2026` are halves of the same sentence,
 * and the previous arrangement — the date in a card down the right-hand column,
 * under the preview — put them a scroll apart.
 *
 * **Save draft is unguarded; Publish is confirmed.** A draft changes nothing
 * anyone records, so making the author confirm it would be ceremony. Publishing
 * freezes a version that entries will post under and closes the previous one's
 * effectivity — irreversible, and the dialog says the two things that are
 * easiest to get wrong about it: which version number it creates, and that the
 * version in force **stays on every entry already posted under it**.
 *
 * That second fact is also stated on the screen itself, in `PublishNotice`,
 * because it is what makes the whole editor safe to use and nobody should have
 * to open a dialog to learn it.
 */
export const EditorToolbar: React.FC<EditorToolbarProps> = ({
    nextVersion,
    effectiveFrom,
    onEffectiveFromChange,
    effectiveFromError,
    canWrite,
    isDirty,
    isSaving,
    isPublishing,
    onOpenHistory,
    onSaveDraft,
    onPublish,
}) => {
    const { t } = useTranslation('pages');
    const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);

    return (
        <div
            className="flex flex-col items-end gap-1.5"
            {...getNodeTestIdProp('operation-editor-toolbar')}
        >
            <div className="flex flex-wrap items-center justify-end gap-2.5">
                {/* The kit's own picker, like every other date in the app:
                    it carries the DD/MM segment order the UI language asks for
                    and owns its refusal message, so the strip does not repeat
                    it. */}
                {canWrite ? (
                    <div className="w-52">
                        <DatePickerField
                            error={effectiveFromError ?? undefined}
                            id="operation-editor-effective-from"
                            label={t('operations.editor.effectiveFrom')}
                            name="effectiveFrom"
                            onChange={onEffectiveFromChange}
                            value={effectiveFrom}
                        />
                    </div>
                ) : null}
                <div className="flex flex-wrap items-center justify-end gap-2.5 mt-7">
                    <Button
                        onPress={onOpenHistory}
                        size="sm"
                        variant="outline"
                        {...getNodeTestIdProp('operation-editor-history')}
                    >
                        <Icon
                            aria-hidden
                            icon="gravity-ui:clock-arrow-rotate-left"
                        />
                        {t('operations.editor.history')}
                    </Button>

                    {canWrite ? (
                        <>
                            <Button
                                isDisabled={
                                    !isDirty || isSaving || isPublishing
                                }
                                onPress={onSaveDraft}
                                size="sm"
                                variant="outline"
                                {...getNodeTestIdProp(
                                    'operation-editor-save-draft',
                                )}
                            >
                                <Icon
                                    aria-hidden
                                    icon="gravity-ui:floppy-disk"
                                />
                                {t('operations.editor.saveDraft')}
                            </Button>
                            <Button
                                // Not gated on `isDirty`, unlike Save draft:
                                // publishing re-affirms whatever is on screen
                                // as a new version — even unedited, that is a
                                // legitimate act (a later effective date for
                                // the same recipe), not a no-op the way
                                // re-saving an unchanged draft would be.
                                isDisabled={isSaving || isPublishing}
                                onPress={() => setIsConfirmOpen(true)}
                                size="sm"
                                variant="primary"
                                {...getNodeTestIdProp(
                                    'operation-editor-publish',
                                )}
                            >
                                {t('operations.editor.publishVersion', {
                                    version: nextVersion,
                                })}
                            </Button>
                        </>
                    ) : null}
                </div>
            </div>

            <Modal
                isOpen={isConfirmOpen}
                onOpenChange={(open) => setIsConfirmOpen(open)}
            >
                <Modal.Backdrop>
                    <Modal.Container size="md">
                        <Modal.Dialog
                            className="w-full max-w-lg"
                            {...getNodeTestIdProp(
                                'operation-editor-publish-confirm',
                            )}
                        >
                            <Modal.CloseTrigger />
                            <Modal.Header>
                                <Modal.Heading>
                                    {t('operations.editor.confirm.title', {
                                        next: nextVersion,
                                    })}
                                </Modal.Heading>
                            </Modal.Header>
                            <Modal.Body className="flex flex-col gap-3 pb-2">
                                <p className="text-sm text-foreground">
                                    {t('operations.editor.confirm.body', {
                                        next: nextVersion,
                                        date: effectiveFrom,
                                    })}
                                </p>
                                {/* The fact that is easiest to get wrong, and
                                    the reason the whole screen is safe to use:
                                    publishing does not rewrite history. */}
                                <p
                                    className="text-sm text-muted"
                                    {...getNodeTestIdProp(
                                        'operation-editor-confirm-history',
                                    )}
                                >
                                    {t('operations.editor.confirm.history')}
                                </p>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button
                                    onPress={() => setIsConfirmOpen(false)}
                                    variant="tertiary"
                                    {...getNodeTestIdProp(
                                        'operation-editor-publish-cancel',
                                    )}
                                >
                                    {t('operations.editor.confirm.cancel')}
                                </Button>
                                <Button
                                    onPress={() => {
                                        setIsConfirmOpen(false);
                                        onPublish();
                                    }}
                                    variant="primary"
                                    {...getNodeTestIdProp(
                                        'operation-editor-publish-confirm-action',
                                    )}
                                >
                                    {t('operations.editor.confirm.confirm')}
                                </Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal>
        </div>
    );
};
